import api from './api';
import { Barber, BarberRegistrationRequest, BarberApprovalRequest, ProfileUpdateRequest, PasswordChangeRequest, BarberServiceRequest, ScheduleRequest } from '../types/barber.types';

export const barberService = {
    // Get all active barbers
    getAllBarbers: async (): Promise<Barber[]> => {
        const response = await api.get<Barber[]>('/barbers');
        return response.data;
    },

    // Get all barbers including inactive (admin only)
    getAllBarbersAdmin: async (): Promise<Barber[]> => {
        const response = await api.get<Barber[]>('/barbers/all');
        return response.data;
    },

    // Get barbers by branch
    getBarbersByBranch: async (branchId: number): Promise<Barber[]> => {
        const response = await api.get<Barber[]>(`/barbers/branch/${branchId}`);
        return response.data;
    },

    // Get barber by ID
    getBarberById: async (id: number): Promise<Barber> => {
        const response = await api.get<Barber>(`/barbers/${id}`);
        return response.data;
    },

    // Register as barber (pre-approval)
    registerAsBarber: async (request: BarberRegistrationRequest): Promise<Barber> => {
        const response = await api.post<Barber>('/barbers/register', request);
        return response.data;
    },

    // Get pending barbers (superuser only)
    getPendingBarbers: async (): Promise<Barber[]> => {
        const response = await api.get<Barber[]>('/barbers/pending');
        return response.data;
    },

    // Approve barber (superuser only)
    approveBarber: async (request: BarberApprovalRequest): Promise<Barber> => {
        const { barberId, ...approvalData } = request;
        const response = await api.put<Barber>(`/barbers/${barberId}/approve`, approvalData);
        return response.data;
    },

    // Reject barber (superuser only)
    rejectBarber: async (barberId: number): Promise<void> => {
        await api.put(`/barbers/${barberId}/reject`);
    },

    // Update barber details (superuser only)
    updateBarber: async (id: number, updates: Partial<Barber>): Promise<Barber> => {
        const response = await api.put<Barber>(`/barbers/${id}`, updates);
        return response.data;
    },

    // Suspend/Activate barber (superuser only)
    updateBarberStatus: async (id: number, status: string): Promise<Barber> => {
        const response = await api.put<Barber>(`/barbers/${id}/status`, { status });
        return response.data;
    },

    // Get my barber status
    getMyStatus: async (): Promise<Barber> => {
        const response = await api.get<Barber>('/barbers/my-status');
        return response.data;
    },

    // Update profile
    updateProfile: async (request: ProfileUpdateRequest): Promise<Barber> => {
        const response = await api.put<Barber>('/barbers/profile', request);
        return response.data;
    },

    // Change password
    changePassword: async (request: PasswordChangeRequest): Promise<void> => {
        await api.put('/barbers/password', request);
    },

    // Update schedules (superuser only)
    updateSchedules: async (id: number, schedules: ScheduleRequest[]): Promise<Barber> => {
        const response = await api.put<Barber>(`/barbers/${id}/schedules`, schedules);
        return response.data;
    },

    // Update barber profile (admin only)
    updateBarberProfile: async (id: number, data: ProfileUpdateRequest): Promise<Barber> => {
        const response = await api.put<Barber>(`/barbers/${id}/profile`, data);
        return response.data;
    },

    // Upload profile image
    uploadProfileImage: async (file: File): Promise<{ url: string; filename: string }> => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post<{ url: string; filename: string }>('/uploads/profile-image', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    // Reset barber password (admin only)
    resetPassword: async (id: number): Promise<string> => {
        const response = await api.post<string>(`/barbers/${id}/reset-password`);
        return response.data;
    },

    // Delete/Deactivate barber (admin only - soft delete)
    deleteBarber: async (id: number): Promise<void> => {
        await api.put(`/barbers/${id}/deactivate`);
    },

    // Reactivate barber (admin only)
    reactivateBarber: async (id: number): Promise<Barber> => {
        const response = await api.put<Barber>(`/barbers/${id}/reactivate`);
        return response.data;
    },

    // Update barber services (admin only)
    updateBarberServices: async (id: number, services: BarberServiceRequest[]): Promise<Barber> => {
        const response = await api.put<Barber>(`/barbers/${id}/services`, services);
        return response.data;
    },
};
